import {
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  Tooltip,
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tab,
} from "@mui/material";

import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { isNotNull } from "../../../../../helpers/isNotNull";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../components/formsComponents";
import {
  formatCurrencyInIndian,
  formatWithCommas,
} from "../../../../../helpers/formatWithCommas";
import { DiscountAndTaxField } from "../../../../../helpers/DiscoutnAndTaxField";
import { CustomDatePicker } from "../../../../../components/formsComponents/CustomDatePicker";
import { IPaymentDetails } from "../../../../../redux/paymentDetails/paymentDetails.types";
import { CancellationReasonAutoSearch } from "../../../../../components/AutoCompleteSearches";
import moment from "moment";

export interface IPaymentDetailsDialogDataProps {
  values: IPaymentDetails;
  errors: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  setValues: (values: any) => void;
  setFieldValue: (field: string, value: any) => void;
  payment_type: string;
  isAdvance?: boolean;
}

export const PaymentDetailsData: React.FC<IPaymentDetailsDialogDataProps> = (
  props
) => {
  const { isAdvance, setFieldValue, values, errors, setValues, payment_type } =
    props;
  const [selectedSign, setSelectedSign] = useState<"+" | "-">("+");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openPaymentTypeDialog, setOpenPaymentTypeDialog] = useState(false);

  const advancePrefix =
    payment_type === "CANCELLATION_ADJUSTMENT" ? "" : "Advance Amount:";

  const handleSignChange = (sign: "+" | "-") => {
    setSelectedSign(sign);
    setFieldValue("transaction_effect", selectedSign);
  };

  React.useEffect(() => {
    const amountStr = values.amount;
    const taxPercentageStr = values.tax_amount_percentage;

    let effectiveAmount = 0;
    let taxAmount = 0;

    if (isNotNull(taxPercentageStr) > 0) {
      taxAmount = (Number(amountStr) * Number(taxPercentageStr)) / 100;
      effectiveAmount = Number(amountStr) + taxAmount;
    } else {
      taxAmount = Number(values.tax_amount);
      effectiveAmount = Number(amountStr) + taxAmount;
    }

    setValues({
      ...values,
      effective_amount: effectiveAmount.toString(),
      ...(isNotNull(taxPercentageStr) > 0 && {
        tax_amount: taxAmount.toString(),
      }),
    });
  }, [
    values.amount,
    values.tax_amount_percentage,
    values.tax_amount,
    setFieldValue,
  ]);

  React.useEffect(() => {
    setFieldValue("payment_type", payment_type);
  }, [setFieldValue, payment_type]);

  React.useEffect(() => {
    if (payment_type === "POSTPONED_ADJUSTMENT") {
      setOpenPaymentTypeDialog(true);
    }
  }, [setFieldValue, payment_type]);

  React.useEffect(() => {
    if (payment_type === "CANCELLATION_ADJUSTMENT") {
      setSelectedSign("-");
      setFieldValue("transaction_effect", "-");
    }
  }, [setFieldValue, payment_type]);

  return (
    <>
      <Grid container spacing={1} sx={{ overflow: "hidden" }}>
        <Grid item xs={4} md={1.5}>
          <ButtonGroup
            sx={{
              mt: 5.2,
              display: "flex",
              justifyContent: "center",
              boxShadow: "none",
              border: "1px solid #e0e0e0",
            }}
            fullWidth
          >
            <Tooltip title="Add" arrow>
              <Button
                onClick={() => handleSignChange("+")}
                sx={{
                  textTransform: "none",
                  backgroundColor:
                    selectedSign === "+" ? "primary.main" : "transparent",
                  color:
                    selectedSign === "+"
                      ? "primary.contrastText"
                      : "text.primary",
                  border: "none",
                  "&:hover": {
                    backgroundColor:
                      selectedSign === "+" ? "primary.main" : "transparent",
                  },
                }}
              >
                <AddIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Deduct" arrow>
              <Button
                onClick={() => {
                  handleSignChange("-");
                  setOpenConfirmDialog(true);
                }}
                sx={{
                  textTransform: "none",
                  backgroundColor:
                    selectedSign === "-" ? "primary.main" : "transparent",
                  color:
                    selectedSign === "-"
                      ? "primary.contrastText"
                      : "text.primary",
                  border: "none",
                  "&:hover": {
                    backgroundColor:
                      selectedSign === "-" ? "primary.main" : "transparent",
                  },
                }}
              >
                <RemoveIcon />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Grid>

        <Grid item xs={8} md={3.5}>
          <CustomFormLabel>Amount</CustomFormLabel>

          <CustomTextField
            name="amount"
            type="string"
            fullWidth
            value={formatCurrencyInIndian(values.amount)}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/[^0-9]/g, "");

              const numericValue =
                rawValue === "" ? "" : String(Number(rawValue));

              setFieldValue("amount", numericValue);
            }}
            size="small"
            error={errors.amount ? true : false}
            helperText={errors.amount}
          />
        </Grid>

        <Grid item xs={12} md={7}>
          <DiscountAndTaxField
            percentageTitle="Tax Percentage"
            percentage={values.tax_amount_percentage}
            onPercentageChange={(val) =>
              setFieldValue("tax_amount_percentage", val)
            }
            percentageValueTitle="Tax Amount"
            percentageValue={values.tax_amount}
            onPercentageValueChange={(val) => setFieldValue("tax_amount", val)}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          {/* <CustomFormLabel>Paid Amount</CustomFormLabel> */}
          <CustomTextField
            label="Paid Amount"
            name="effective_amount"
            type="string"
            disabled
            fullWidth
            value={formatCurrencyInIndian(values.effective_amount)}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/[^0-9]/g, "");

              setFieldValue("effective_amount", e.target.value);
            }}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          {/* <CustomFormLabel>Payment Date</CustomFormLabel> */}
          <CustomDatePicker
            placeholder="Payment Date"
            fullWidth
            value={values.payment_date || ""}
            onChange={(newValue) => {
              setFieldValue("payment_date", newValue);
            }}
            errorMessage={errors.payment_date}
            // minDate={moment().startOf("day")}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          {/* <CustomFormLabel>Billing To</CustomFormLabel> */}
          <ControlledCustomSelect
            label="Billing To"
            variant="outlined"
            size="small"
            fullWidth
            displayEmpty
            value={values.billing_to}
            onChange={(e) => {
              setFieldValue("billing_to", e.target.value);
            }}
            placeholder="Select Billing To"
            options={[
              { label: "Bill to Company", value: "BILL_TO_COMPANY" },
              { label: "Direct", value: "DIRECT" },
            ]}
            error={errors.billing_to ? true : false}
            helperText={errors.billing_to}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          {/* <CustomFormLabel>Mode of Payment</CustomFormLabel> */}
          <ControlledCustomSelect
            label="Mode of Payment"
            variant="outlined"
            size="small"
            fullWidth
            displayEmpty
            value={values.mode_of_payment}
            onChange={(e) => {
              setFieldValue("mode_of_payment", e.target.value);
            }}
            placeholder="Select Payment Mode"
            options={[
              { label: "Vendor Payment", value: "VENDOR_PAYMENT" },
              { label: "Card", value: "CARD" },
              { label: "Upi", value: "UPI" },
              { label: "Bank Transfer", value: "BANK_TRANSFER" },
              { label: "Other", value: "OTHER" },
            ]}
            error={errors.mode_of_payment ? true : false}
            helperText={errors.mode_of_payment}
          />
        </Grid>
        {payment_type === "CANCELLATION_ADJUSTMENT" ||
        payment_type === "POSTPONED_ADJUSTMENT" ? (
          <Grid item xs={12} md={6}>
            <CancellationReasonAutoSearch
              required={true}
              label="Cancellation Reason"
              value={values.cancellation_dead_reason ?? ""}
              disabled={
                payment_type !== "CANCELLATION_ADJUSTMENT" &&
                payment_type !== "POSTPONED_ADJUSTMENT"
              } // Disable unless it's cancellation
              placeholder="Search cancellation reasons..."
              error={
                (payment_type === "CANCELLATION_ADJUSTMENT" ||
                  payment_type === "POSTPONED_ADJUSTMENT") &&
                !values.cancellation_dead_reason
              }
              helperText={
                (payment_type === "CANCELLATION_ADJUSTMENT" ||
                  payment_type === "POSTPONED_ADJUSTMENT") &&
                !values.cancellation_dead_reason
                  ? "Cancellation Reason is required"
                  : ""
              }
              onSelect={(selectedReason) => {
                if (selectedReason && selectedReason.name) {
                  setFieldValue(
                    "cancellation_dead_reason",
                    selectedReason.name
                  );
                } else {
                  setFieldValue("cancellation_dead_reason", "");
                }
              }}
            />
          </Grid>
        ) : (
          ""
        )}

        <Grid item xs={12} md={6}>
          <CustomTextField
            label="Remarks"
            name="remarks"
            type="string"
            fullWidth
            value={
              isAdvance && !values.remarks.startsWith(advancePrefix)
                ? advancePrefix
                : values.remarks
            }
            onChange={(e) => {
              let newValue = e.target.value;

              if (isAdvance) {
                if (!newValue.startsWith(advancePrefix)) {
                  newValue = advancePrefix;
                }
              }

              setFieldValue("remarks", newValue);
            }}
            error={!!errors.remarks}
            helperText={errors.remarks}
          />
        </Grid>
      </Grid>

      <MuiDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirm Deduction</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deduct amount ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirmDialog(false);
              handleSignChange("+");
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSignChange("-");
              setOpenConfirmDialog(false);
            }}
            color="primary"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
};
